import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  alpha,
} from "@mui/material";
import ParkIcon from '@mui/icons-material/Park';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CropFreeIcon from '@mui/icons-material/CropFree';
import FenceIcon from '@mui/icons-material/Fence';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ForestIcon from '@mui/icons-material/Forest';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import YardIcon from '@mui/icons-material/Yard';
import TerrainIcon from '@mui/icons-material/Terrain';
import "../../i8n"
import { useTranslation } from "react-i18next"
import { GetApi } from '../../Services/api/ApiCall';


export default function HomePage() {
  const { t, i18n } = useTranslation();
  const colors = {
    darkGreen: "#1B5E20",
    mediumGreen: "#2E7D32",
    lightGreen: "#4CAF50",
    paleGreen: "#E8F5E9",
    darkOrange: "#E65100",
    mediumOrange: "#F57C00",
    lightOrange: "#FF9800",
    paleOrange: "#FFF3E0",
    white: "#FFFFFF",
    darkText: "#1a1a1a",
    subText: "#4a4a4a",
    cardBg: "#FAFFFE",
  };
  
  
  function HeroBanner() { 
    const [slides, setSlides] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
      const getSlideImages = async () => {
        try {
          const result = await GetApi(`/dynamic/dashboard-carousel`);
          setSlides(result || []);
        } catch (error) {
          console.log("error on get slides", error);
        }
      };
      getSlideImages();
    }, []);

    useEffect(() => {
      if (!slides.length) return;
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(interval);
    }, [slides]);

    const currentImage = slides[currentSlide]?.imageurl;

    return (
      <Box
        sx={{
          backgroundImage: currentImage ? `url(${currentImage})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          py: { xs: 6, sm: 8, md: 12 },
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="h3" color='white' sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' }, lineHeight: { xs: 1.3, md: 1.2 } }}>
                {t("title")}
              </Typography>
              <Typography variant="body1" color='white' sx={{ mt: 1.5, fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.7 }}>
                {t("description")}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  function StatsBar() {
    const icons = [<LocalFloristIcon />, <YardIcon />, <AccountTreeIcon />, <TerrainIcon />];
    const stats = t("stats", { returnObjects: true }).map((item, index) => ({
      ...item,
      icon: icons[index],
    }));
    return (
      <Box sx={{ background: `linear-gradient(135deg, ${colors.darkGreen} 0%, #1a4a1c 100%)`, py: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg" sx={{ maxWidth: { xs: '100%', sm: 'lg' }, px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            {stats.map((stat, i) => (
              <Grid size={{ xs: 6, md: 3 }} key={i}>
                <Box sx={{
                  textAlign: "center", position: "relative",
                  "&:not(:last-child)::after": {
                    content: '""', position: "absolute", right: 0, top: "15%", height: "70%", width: 1,
                    bgcolor: alpha(colors.white, 0.1), display: { xs: "none", md: "block" }
                  }
                }}>
                  <Avatar sx={{ width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 }, mx: "auto", mb: 0.8, bgcolor: alpha(colors.lightOrange, 0.15) }}>
                    <Box sx={{ color: colors.lightOrange, "& .MuiSvgIcon-root": { fontSize: { xs: 18, md: 22 } } }}>{stat.icon}</Box>
                  </Avatar>
                  <Typography sx={{ color: colors.lightOrange, fontWeight: 800, fontSize: { xs: "1.2rem", md: "2rem" }, lineHeight: 1.2 }}>{stat.value}</Typography>
                  <Typography sx={{ color: alpha(colors.white, 0.7), fontSize: { xs: "0.65rem", md: "0.85rem" }, fontWeight: 500, lineHeight: 1.3 }}>{stat.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  function PrioritySpeciesSection() {
    const species = t("prioritySpecies", { returnObjects: true });
    const headers = [
      t("speciesTable.headers.priorityCategory"),
      t("speciesTable.headers.commonName"),
      t("speciesTable.headers.scientificName")
    ];
    return (
      <Box sx={{ py: { xs: 5, md: 10 }, bgcolor: colors.white, overflow: 'hidden' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: { xs: 3, md: 5 } }}>
            <Chip
              icon={<LocalFloristIcon />}
              label={t('speciesCatalogue.title')}
              sx={{
                bgcolor: colors.paleGreen, color: colors.darkGreen, fontWeight: 700,
                fontSize: { xs: "0.7rem", md: "0.8rem" }, py: 2.5, px: 1, mb: 1.5,
                "& .MuiChip-icon": { color: colors.darkGreen }
              }}
            />
            <Typography variant="h4" sx={{
              fontWeight: 800, color: colors.darkGreen,
              fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.5rem" }, mb: 1, lineHeight: 1.3
            }}>
              {t('speciesCatalogue.subtitle')}
            </Typography>
            <Typography variant="body1" sx={{
              color: colors.subText, maxWidth: 700, mx: "auto",
              lineHeight: 1.7, fontSize: { xs: "0.85rem", md: "1rem" }, px: { xs: 1, md: 0 }
            }}>
              {t('speciesCatalogue.description')}
            </Typography>
          </Box>

          <Card
            elevation={0}
            sx={{
              borderRadius: { xs: 2.5, md: 4 },
              overflow: "hidden",
              border: "1.5px solid",
              borderColor: alpha(colors.lightGreen, 0.25),
              // ★ KEY FIX: contain the overflow within the card on mobile
              maxWidth: '100%',
            }}
          >
            <Box sx={{
              background: `linear-gradient(135deg, ${colors.darkGreen}, ${colors.mediumGreen})`,
              px: { xs: 2, md: 3 }, py: { xs: 1.5, md: 2 },
              display: "flex", alignItems: "center", gap: 1.5
            }}>
              <ParkIcon sx={{ color: colors.lightOrange, fontSize: { xs: 22, md: 28 } }} />
              <Typography sx={{ color: colors.white, fontWeight: 700, fontSize: { xs: "0.85rem", md: "1rem" } }}>
                {t('speciesTable.title')}
              </Typography>
            </Box>

            {/* ★ KEY FIX: wrap TableContainer in a scrollable Box for mobile */}
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <TableContainer sx={{ maxHeight: { xs: 400, md: 520 }, minWidth: { xs: 420, md: 'auto' } }}>
                <Table stickyHeader size={typeof window !== 'undefined' && window.innerWidth < 600 ? 'small' : 'medium'}>
                  <TableHead>
                    <TableRow>
                      {headers.map((head) => (
                        <TableCell key={head} sx={{
                          fontWeight: 700, color: colors.darkGreen, bgcolor: colors.paleGreen,
                          fontSize: { xs: "0.7rem", md: "0.85rem" },
                          textTransform: "uppercase", letterSpacing: 0.5,
                          borderBottom: `2px solid ${alpha(colors.darkGreen, 0.1)}`,
                          whiteSpace: 'nowrap'
                        }}>
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {species.map((group) =>
                      group.items.map((item, idx) => {
                        const isFirst = idx === 0;
                        return (
                          <TableRow key={`${group.priority}-${item.common}`} sx={{
                            "&:nth-of-type(even)": { bgcolor: alpha(colors.paleGreen, 0.3) },
                            "&:hover": { bgcolor: alpha(colors.paleOrange, 0.4) },
                            transition: "background 0.2s ease"
                          }}>
                            {isFirst && (
                              <TableCell
                                rowSpan={group.items.length}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: { xs: "0.75rem", md: "0.9rem" },
                                  borderBottom: `2px solid ${alpha(colors.darkGreen, 0.08)}`,
                                  verticalAlign: "middle",
                                  bgcolor: alpha(colors.darkGreen, 0.04),
                                  borderRight: `2px solid ${alpha(colors.darkGreen, 0.1)}`,
                                  // ★ KEY FIX: prevent the chip from forcing column wider on mobile
                                  whiteSpace: 'nowrap',
                                  px: { xs: 1, md: 1.5 }
                                }}
                              >
                                <Chip
                                  label={group.priority}
                                  size="small"
                                  sx={{
                                    bgcolor: colors.paleGreen, color: colors.darkGreen,
                                    fontWeight: 700,
                                    fontSize: { xs: "0.65rem", md: "0.78rem" },
                                    height: "auto", py: 0.4
                                  }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{
                              fontWeight: 600, color: colors.darkText,
                              fontSize: { xs: "0.78rem", md: "0.9rem" },
                              whiteSpace: 'nowrap'
                            }}>
                              {item.common}
                            </TableCell>
                            <TableCell sx={{
                              color: colors.subText,
                              fontSize: { xs: "0.72rem", md: "0.88rem" },
                              fontStyle: "italic",
                              whiteSpace: 'nowrap'
                            }}>
                              {item.scientific}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Card>
        </Container>
      </Box>
    );
  }

  function SchemeDetailsSection() {
    const { t } = useTranslation();
    const icons = [<CropFreeIcon />, <FenceIcon />];
    const colorsArray = [
      { color: colors.darkGreen, bgColor: colors.paleGreen },
      { color: colors.darkOrange, bgColor: colors.paleOrange }
    ];

    const schemeDetails = t("schemeDetails", { returnObjects: true }).map((item, idx) => ({
      ...item,
      icon: icons[idx],
      color: colorsArray[idx].color,
      bgColor: colorsArray[idx].bgColor
    }));

    return (
      <Box sx={{
        py: { xs: 5, md: 10 },
        background: `linear-gradient(180deg, ${colors.paleGreen} 0%, ${colors.white} 100%)`,
        overflow: 'hidden'
      }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: { xs: 3, md: 5 } }}>
            <Chip
              icon={<YardIcon />}
              label={t("schemeDetailsSection.chip")}
              sx={{
                bgcolor: colors.paleGreen, color: colors.darkGreen, fontWeight: 700,
                fontSize: { xs: "0.7rem", md: "0.8rem" }, py: 2.5, px: 1, mb: 1.5,
                "& .MuiChip-icon": { color: colors.darkGreen }
              }}
            />
            <Typography variant="h4" sx={{
              fontWeight: 800, color: colors.darkGreen,
              fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.5rem" }, mb: 1, lineHeight: 1.3
            }}>
              {t("schemeDetailsSection.title")}
            </Typography>
            <Typography variant="body1" sx={{
              fontSize: { xs: "0.85rem", md: t('language') === 'ta' ? '1rem' : '1.1rem' },
              px: { xs: 1, md: 0 }
            }}>
              {t("schemeDetailsSection.description")}
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2.5, md: 4 }} justifyContent="center">
            {schemeDetails.map((scheme, index) => (
              <Grid
                item
                xs={12}
                md={6}
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  // ★ KEY FIX: remove any fixed width, let grid control sizing
                  maxWidth: { xs: '100%', md: 600 },
                  mx: { xs: 'auto', md: 0 }
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: { xs: 2.5, md: 4 },
                    height: "100%",
                    // ★ KEY FIX: removed the hardcoded 500px width
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: 'border-box',
                    overflow: "visible",
                    border: "2px solid",
                    borderColor: alpha(scheme.color, 0.2),
                    borderTop: `4px solid ${scheme.color}`,
                    transition: "all 0.35s ease",
                    cursor: "default",
                    "&:hover": {
                      borderColor: scheme.color,
                      transform: { xs: "none", md: "translateY(-6px)" },
                      boxShadow: { xs: 'none', md: `0 16px 40px ${alpha(scheme.color, 0.15)}` }
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                    {/* Header */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 3 } }}>
                      <Avatar sx={{ width: { xs: 44, md: 56 }, height: { xs: 44, md: 56 }, bgcolor: scheme.bgColor, flexShrink: 0 }}>
                        <Box sx={{ color: scheme.color, "& .MuiSvgIcon-root": { fontSize: { xs: 24, md: 30 } } }}>{scheme.icon}</Box>
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Chip
                          label={`${t("schemeDetailsSection.methodPrefix")} ${scheme.number}`}
                          size="small"
                          sx={{
                            bgcolor: scheme.bgColor, color: scheme.color, fontWeight: 700,
                            fontSize: { xs: "0.65rem", md: "0.72rem" }, mb: 0.4
                          }}
                        />
                        <Typography variant="h6" sx={{
                          fontWeight: 700, color: scheme.color,
                          fontSize: { xs: "0.95rem", md: "1.2rem" },
                          lineHeight: 1.3,
                          // ★ prevent long Tamil text from overflowing
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          {scheme.method}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ borderColor: alpha(scheme.color, 0.1), mb: { xs: 2, md: 3 } }} />

                    {/* Stats grid */}
                    <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
                      {[
                        { label: t("schemeDetailsSection.stats.saplings"), value: scheme.saplings, icon: <AccountTreeIcon sx={{ fontSize: 16 }} /> },
                        { label: t("schemeDetailsSection.stats.area"), value: scheme.area, icon: <CropFreeIcon sx={{ fontSize: 16 }} /> },
                      ].map((stat) => (
                        <Grid item xs={6} key={stat.label}>
                          <Box sx={{
                            p: { xs: 1.5, md: 2 }, borderRadius: { xs: 2, md: 3 },
                            bgcolor: scheme.bgColor, textAlign: "center"
                          }}>
                            <Box sx={{ color: scheme.color, mb: 0.3, "& .MuiSvgIcon-root": { fontSize: { xs: 18, md: 20 } } }}>{stat.icon}</Box>
                            <Typography variant="h5" sx={{
                              fontWeight: 800, color: scheme.color,
                              fontSize: { xs: "1.1rem", md: "1.5rem" },
                              // ★ handle long numbers
                              wordBreak: 'break-word'
                            }}>
                              {stat.value}
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: colors.subText, fontWeight: 600,
                              textTransform: "uppercase", letterSpacing: 0.5,
                              fontSize: { xs: "0.6rem", md: "0.7rem" },
                              lineHeight: 1.3
                            }}>
                              {stat.label}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Focus box */}
                    <Box sx={{
                      p: { xs: 1.5, md: 2.5 },
                      borderRadius: { xs: 2, md: 3 },
                      bgcolor: alpha(scheme.color, 0.04),
                      border: "1px dashed",
                      borderColor: alpha(scheme.color, 0.25),
                      display: "flex",
                      alignItems: "flex-start",
                      gap: { xs: 1, md: 1.5 }
                    }}>
                      <ArrowForwardIcon sx={{ color: scheme.color, fontSize: { xs: 18, md: 20 }, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{
                        color: colors.darkText, lineHeight: 1.7, fontWeight: 500,
                        fontSize: { xs: "0.82rem", md: "0.9rem" },
                        // ★ handle long Tamil text wrapping safely
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {scheme.focus}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Note card */}
          <Card
            elevation={0}
            sx={{
              mt: { xs: 3, md: 4 },
              borderRadius: { xs: 2.5, md: 4 },
              border: "1.5px solid",
              borderColor: alpha(colors.lightOrange, 0.25),
              bgcolor: colors.paleOrange
            }}
          >
            <CardContent sx={{
              p: { xs: 2, sm: 2.5, md: 3.5 },
              display: "flex",
              alignItems: "flex-start",
              gap: { xs: 1.5, md: 2 },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <CheckCircleOutlineIcon sx={{ color: colors.darkOrange, fontSize: { xs: 28, md: 32 }, flexShrink: 0 }} />
              <Typography sx={{
                color: colors.darkText, fontWeight: 500,
                fontSize: { xs: "0.82rem", md: "0.95rem" }, lineHeight: 1.7,
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}>
                {t("schemeDetailsSection.note")}
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: colors.white, display: "flex", flexDirection: "column", overflowX: 'hidden' }}>
      <HeroBanner />
      <StatsBar />
      <PrioritySpeciesSection />
      <SchemeDetailsSection />
    </Box>
  );
}